import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Calendar, Clock, User, Mail, Phone, MapPin, CheckCircle, 
  XCircle, AlertCircle, X, MessageSquare, Send, Bell,
  FileText, Building2, Train, ExternalLink, Copy, Check
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";

const STATUS_CONFIG = {
  pending: { label: "Ausstehend", color: "bg-yellow-100 text-yellow-800 border-yellow-300", icon: AlertCircle, iconColor: "text-yellow-600" },
  confirmed: { label: "Bestätigt", color: "bg-green-100 text-green-800 border-green-300", icon: CheckCircle, iconColor: "text-green-600" },
  cancelled: { label: "Abgesagt", color: "bg-red-100 text-red-800 border-red-300", icon: XCircle, iconColor: "text-red-600" },
  completed: { label: "Erschienen", color: "bg-blue-100 text-blue-800 border-blue-300", icon: CheckCircle, iconColor: "text-blue-600" },
  no_show: { label: "Nicht erschienen", color: "bg-gray-100 text-gray-800 border-gray-300", icon: XCircle, iconColor: "text-gray-600" }
};

const InfoRow = ({ icon: Icon, label, value, iconColor = "text-slate-400", copyable = false }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success("Kopiert!");
    setTimeout(() => setCopied(false), 2000);
  };
  
  if (!value) return null;
  
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className={`w-5 h-5 ${iconColor} mt-0.5 flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
        <p className="text-sm font-medium text-slate-900 break-words">{value}</p>
      </div>
      {copyable && (
        <button 
          onClick={handleCopy}
          className="p-1 hover:bg-slate-100 rounded transition-colors"
          title="Kopieren"
        >
          {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-400" />}
        </button>
      )}
    </div>
  );
};

const TimelineItem = ({ icon: Icon, title, time, status, statusColor }) => (
  <div className="flex items-start gap-3 py-2">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
      status === 'done' ? 'bg-green-100' : status === 'pending' ? 'bg-yellow-100' : 'bg-slate-100'
    }`}>
      <Icon className={`w-4 h-4 ${
        status === 'done' ? 'text-green-600' : status === 'pending' ? 'text-yellow-600' : 'text-slate-400'
      }`} />
    </div>
    <div className="flex-1">
      <p className="text-sm font-medium text-slate-900">{title}</p>
      <p className="text-xs text-slate-500">{time}</p>
    </div>
    {status === 'done' && (
      <Badge className="bg-green-100 text-green-700 text-xs">Erledigt</Badge>
    )}
    {status === 'pending' && (
      <Badge className="bg-yellow-100 text-yellow-700 text-xs">Ausstehend</Badge>
    )}
  </div>
);

export const AppointmentDetailModal = ({ 
  appointment, 
  isOpen, 
  onClose, 
  onStatusChange, 
  onDelete,
  onSendReminder 
}) => {
  if (!appointment) return null;
  
  const statusConfig = STATUS_CONFIG[appointment.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;
  
  // Format dates for display
  const formatDateTime = (dateStr, timeStr) => {
    if (!dateStr) return "Nicht festgelegt";
    return `${dateStr}${timeStr ? ` um ${timeStr} Uhr` : ''}`;
  };
  
  // Calculate reminder date (1 day before)
  const getReminderInfo = () => {
    // This would be calculated based on actual appointment date
    return {
      date: "Einen Tag vor dem Termin",
      time: "12:00 - 13:00 Uhr",
      status: appointment.reminder_sent ? 'done' : 'pending'
    };
  };
  
  const reminderInfo = getReminderInfo();
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-4 border-b border-slate-200">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center text-white text-xl font-bold shadow-lg">
                {appointment.first_name?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-slate-900">
                  {appointment.first_name}
                </DialogTitle>
                <p className="text-sm text-slate-500">Bewerber-Termin</p>
              </div>
            </div>
            <Badge className={`${statusConfig.color} border flex items-center gap-1.5 px-3 py-1.5`}>
              <StatusIcon className={`w-4 h-4 ${statusConfig.iconColor}`} />
              {statusConfig.label}
            </Badge>
          </div>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
          {/* Left Column - Termin Details */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Termin-Details
            </h3>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-lg bg-blue-500 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-lg font-bold text-slate-900">{appointment.appointment_date || "Nicht festgelegt"}</p>
                  <p className="text-blue-600 font-semibold">{appointment.appointment_time ? `${appointment.appointment_time} Uhr` : ""}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-2 text-sm text-slate-600 bg-white/60 rounded-lg p-3">
                <MapPin className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">HYPEFactory - Hauptstelle</p>
                  <p>Grafenberger Allee 293</p>
                  <p>40237 Düsseldorf</p>
                  <p className="text-xs text-slate-500 mt-1">8. Etage - Personalbüro</p>
                </div>
              </div>
            </div>
            
            {/* Anfahrt Info */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <Train className="w-4 h-4 text-slate-500" />
                Anfahrt
              </h4>
              <p className="text-sm text-slate-600">
                Vom Hbf Düsseldorf: <strong>Straßenbahn Linie 709</strong> (Richtung Gerresheim Krankenhaus) 
                bis <strong>"Schlüterstraße/AA"</strong>
              </p>
            </div>
          </div>
          
          {/* Right Column - Kontakt & Status */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
              <User className="w-4 h-4" />
              Kontaktdaten
            </h3>
            
            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
              <InfoRow icon={User} label="Name" value={appointment.first_name} />
              <InfoRow icon={Mail} label="E-Mail" value={appointment.email} copyable />
              <InfoRow icon={Phone} label="Telefon" value={appointment.phone} copyable />
            </div>
            
            {/* Session/Lead Info */}
            {(appointment.session_id || appointment.lead_id) && (
              <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                <p className="text-xs text-slate-500">
                  {appointment.session_id && <span>Session: {appointment.session_id.substring(0, 8)}...</span>}
                  {appointment.lead_id && <span className="ml-2">Lead: {appointment.lead_id.substring(0, 8)}...</span>}
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Timeline / Kommunikation */}
        <div className="py-4 border-t border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Kommunikation & Timeline
          </h3>
          
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-1">
            <TimelineItem 
              icon={FileText}
              title="Termin gebucht"
              time={appointment.created_at ? new Date(appointment.created_at).toLocaleString('de-DE') : "Unbekannt"}
              status="done"
            />
            
            <TimelineItem 
              icon={Mail}
              title="Bestätigungs-E-Mail gesendet"
              time={appointment.confirmation_sent_at ? new Date(appointment.confirmation_sent_at).toLocaleString('de-DE') : (appointment.confirmation_sent ? "Gesendet" : "Nicht gesendet")}
              status={appointment.confirmation_sent ? 'done' : 'pending'}
            />
            
            <TimelineItem 
              icon={Bell}
              title="Erinnerung (1 Tag vorher)"
              time={`${reminderInfo.date}, ${reminderInfo.time} - Anruf unter 0175 6001719`}
              status={reminderInfo.status}
            />
            
            <TimelineItem 
              icon={Send}
              title="SMS-Erinnerung"
              time={appointment.sms_sent_at ? new Date(appointment.sms_sent_at).toLocaleString('de-DE') : "Noch nicht gesendet"}
              status={appointment.sms_sent ? 'done' : 'pending'}
            />
            
            <TimelineItem 
              icon={Building2}
              title="Termin / Erscheinen"
              time={formatDateTime(appointment.appointment_date, appointment.appointment_time)}
              status={appointment.status === 'completed' ? 'done' : appointment.status === 'no_show' ? 'failed' : 'pending'}
            />
          </div>
        </div>
        
        {/* Mitbringen Checkliste */}
        <div className="py-4 border-t border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Bewerber muss mitbringen
          </h3>
          
          <div className="grid grid-cols-2 gap-2 text-sm">
            {[
              "Personalausweis",
              "Führerschein (falls vorhanden)",
              "Sozialversicherungsnummer",
              "Steuer-ID",
              "Krankenkassenkarte",
              "Reisegepäck (ca. 2-3 Wochen)"
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 text-slate-600 bg-slate-50 rounded-lg px-3 py-2">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="pt-4 border-t border-slate-200 flex flex-wrap gap-2">
          <Button
            onClick={() => onStatusChange(appointment.id, "confirmed")}
            className="bg-green-600 hover:bg-green-700 text-white"
            disabled={appointment.status === 'confirmed'}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Bestätigen
          </Button>
          
          <Button
            onClick={() => onStatusChange(appointment.id, "completed")}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={appointment.status === 'completed'}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Erschienen
          </Button>
          
          <Button
            onClick={() => onStatusChange(appointment.id, "no_show")}
            variant="outline"
            className="border-gray-300 text-gray-700"
            disabled={appointment.status === 'no_show'}
          >
            <XCircle className="w-4 h-4 mr-2" />
            Nicht erschienen
          </Button>
          
          <Button
            onClick={() => onStatusChange(appointment.id, "cancelled")}
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-50"
            disabled={appointment.status === 'cancelled'}
          >
            <XCircle className="w-4 h-4 mr-2" />
            Absagen
          </Button>
          
          <div className="flex-1" />
          
          <Button
            onClick={() => {
              if (window.confirm("Termin wirklich löschen?")) {
                onDelete(appointment.id);
                onClose();
              }
            }}
            variant="ghost"
            className="text-red-600 hover:bg-red-50"
          >
            Löschen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentDetailModal;
